import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import LoadingSpinner from '../components/LoadingSpinner';
import MediaSlider from '../components/MediaSlider';
import { apiService } from '../services/api';

const ProductDetail = () => {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  // Metni formatla - alt satır geçişlerini handle et
  const formatText = (text) => {
    if (!text) return '';
    return text.replace(/\n/g, '<br />');
  };

  // Modal fonksiyonları
  const openImageModal = (imageSrc, index = 0) => {
    setSelectedImage(imageSrc);
    setSelectedImageIndex(index);
  };

  const closeImageModal = () => {
    setSelectedImage(null);
    setSelectedImageIndex(0);
  };

  // Modal içinde resim navigasyonu
  const goToNextImage = () => {
    const mediaItems = getProductMediaItems(product);
    if (mediaItems.length > 1) {
      const nextIndex = (selectedImageIndex + 1) % mediaItems.length;
      setSelectedImageIndex(nextIndex);
      setSelectedImage(mediaItems[nextIndex]);
    }
  };

  const goToPrevImage = () => {
    const mediaItems = getProductMediaItems(product);
    if (mediaItems.length > 1) {
      const prevIndex = selectedImageIndex === 0 ? mediaItems.length - 1 : selectedImageIndex - 1;
      setSelectedImageIndex(prevIndex);
      setSelectedImage(mediaItems[prevIndex]);
    }
  };

  // Ürün medya öğelerini hazırla
  const getProductMediaItems = (product) => {
    if (!product) return [];
    
    const mediaItems = [];
    
    // API'den gelen medya array'ini kullan
    if (product.media && Array.isArray(product.media)) {
      product.media.forEach(media => {
        if (media.url) {
          mediaItems.push(media.url);
        }
      });
    }
    
    // Eğer media array'i yoksa ama ana resim varsa onu kullan
    if (mediaItems.length === 0 && product.image) {
      mediaItems.push(product.image);
    }
    
    // Eğer hiç medya yoksa placeholder döndür
    if (mediaItems.length === 0) {
      return [null]; // MediaSlider boş state gösterecek
    }
    
    return mediaItems;
  };

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true);
        const response = await apiService.getProductById(id);
        setProduct(response.data?.product);
      } catch (err) {
        console.error('Ürün yüklenirken hata:', err);
        setError('Ürün bilgileri yüklenirken bir hata oluştu.');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchProduct();
      // Sayfa en üstten başlasın
      window.scrollTo(0, 0);
    }
  }, [id]);

  if (loading) {
    return <LoadingSpinner message="Ürün yükleniyor..." />;
  }

  if (error || !product) {
    return (
      <div className="container py-5">
        <div className="alert alert-danger text-center">
          <h4>Hata</h4>
          <p>{error || 'Ürün bulunamadı.'}</p>
          <Link to="/products" className="btn btn-primary">
            Ürünlere Dön
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="product-detail-clean">
      {/* Navigation */}
      <div className="container pt-4 pb-2">
        <nav className="simple-breadcrumb">
          <Link to="/" className="nav-link">Ana Sayfa</Link>
          <span className="separator">→</span>
          <Link to="/products" className="nav-link">Ürünler</Link>
          <span className="separator">→</span>
          <span className="current">{product.name}</span>
        </nav>
      </div>

      {/* Main Content */}
      <div className="container pb-5">
        <div className="row gy-4">
          {/* Image Section */}
          <div className="col-lg-7">
            <div className="product-image-section">
              <div className="main-image-container">
                <MediaSlider
                  mediaItems={getProductMediaItems(product)}
                  className="clean-slider"
                  showThumbnails={true}
                  autoPlay={false}
                  showControls={true}
                  onImageClick={openImageModal}
                />
              </div>
            </div>
          </div>

          {/* Info Section */}
          <div className="col-lg-5">
            <div className="product-info-clean">
              {/* Title & Category */}
              <div className="product-header">
                <h1 className="product-title">{product.name}</h1>
                
                <div className="product-badges">
                  {product.is_featured && (
                    <span className="badge-featured">
                      <i className="bi bi-star-fill"></i>
                      Öne Çıkan
                    </span>
                  )}
                  
                  {product.category_name && (
                    <span className="badge-category">
                      {product.category_name}
                    </span>
                  )}
                </div>
              </div>

              {/* Action Buttons - Moved up */}
              <div className="product-actions">
                <Link to="/contact" className="btn-primary-clean">
                  <i className="bi bi-chat-text me-2"></i>
                  İletişime Geç
                </Link>
                <Link to="/products" className="btn-secondary-clean">
                  <i className="bi bi-arrow-left me-2"></i>
                  Ürünlere Dön
                </Link>
              </div>

              {/* Description */}
              {product.description && (
                <div className="product-description">
                  <h3 className="section-heading">Açıklama</h3>
                  <div 
                    className="description-text" 
                    dangerouslySetInnerHTML={{__html: formatText(product.description)}}
                  />
                </div>
              )}

              {/* Production Method */}
              {product.production_method && (
                <div className="product-detail-section">
                  <h3 className="section-heading">Üretim Yöntemi</h3>
                  <div 
                    className="detail-text" 
                    dangerouslySetInnerHTML={{__html: formatText(product.production_method)}}
                  />
                </div>
              )}

              {/* Materials */}
              {product.materials_used && (
                <div className="product-detail-section">
                  <h3 className="section-heading">Kullanılan Malzemeler</h3>
                  <div 
                    className="detail-text" 
                    dangerouslySetInnerHTML={{__html: formatText(product.materials_used)}}
                  />
                </div>
              )}

            </div>
          </div>
        </div>
      </div>

      {/* Image Modal */}
      {selectedImage && (
        <div className="product-image-modal" onClick={closeImageModal}>
          <div className="product-image-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="product-image-modal-close" onClick={closeImageModal}>
              <i className="bi bi-x-lg"></i>
            </button>
            
            {/* Çoklu resim varsa navigation ekle */}
            {getProductMediaItems(product).length > 1 && (
              <>
                <button className="product-image-modal-prev" onClick={goToPrevImage}>
                  <i className="bi bi-chevron-left"></i>
                </button>
                <button className="product-image-modal-next" onClick={goToNextImage}>
                  <i className="bi bi-chevron-right"></i>
                </button>
              </>
            )}
            
            <img
              src={selectedImage}
              alt={product.name}
              className="product-image-modal-img"
            />
            
            {/* Resim sayacı */}
            {getProductMediaItems(product).length > 1 && (
              <div className="product-image-modal-counter">
                <span className="badge bg-dark bg-opacity-75">
                  {selectedImageIndex + 1} / {getProductMediaItems(product).length}
                </span>
              </div>
            )}
            

          </div>
        </div>
      )}
    </div>
  );
};

export default ProductDetail;
